import React, { useEffect, useRef, useState } from 'react'
import { Button, Checkbox, ExternalLink, Input, Svg } from 'ui'
import { Flex, Spinner, Text } from 'theme-ui'
import { LoginSignUpProps } from './LoginSignUpForm.interface'
import PasswordInput from 'components/PasswordInput'
import { PasswordValidationState, validatePassword } from 'utils/passwordValidation'
import ButtonArrow from 'images/button-arrow-right.svg'
import { MainContentContainer } from 'components/NewStaticPage'
import DividerWithText from 'ui/DividerWithText'
import { LoginSignUpGoogleButton } from 'components/LoginSignUpForm/LoginSignUpGoogleButton'

export default function SignupForm({
  handleUpdate,
  form,
  onNextClick,
  onPreviousClick,
  isRegistrationInProgress,
  children,
}: LoginSignUpProps): React.ReactElement {
  const emailExists: boolean = form.email.length > 0
  const emailInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const passwordConfirmationRef = useRef<HTMLInputElement>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordsEqual, setPasswordsEqual] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordHint, setPasswordHint] = useState('')
  const [passwordValidationState, setPasswordValidationState] = useState(
    PasswordValidationState.EMPTY,
  )
  const [formValid, setFormValid] = useState(false)
  const [isChecked, setIsChecked] = useState(false)

  useEffect(() => {
    const tempPasswordValidationState = validatePassword(password)

    setPasswordValidationState(tempPasswordValidationState)

    setPasswordHint(tempPasswordValidationState.valueOf())

    if (password && tempPasswordValidationState === PasswordValidationState.VALID) {
      if (password === confirmPassword) {
        setPasswordsEqual(true)
      } else {
        setPasswordsEqual(false)
        // We only want to show that the passwords don't match /after/ the user has started typing in the Confirm Password box
        if (confirmPassword) {
          setPasswordHint("Passwords don't match!")
        }
      }
    }
  }, [password, confirmPassword])

  useEffect(() => {
    // artificially trigger a change event once on first render to handle race conditions
    // with automatic input from things like password managers
    handleUpdate({ name: 'email', value: emailInputRef.current?.value || '' })
    handleUpdate({
      name: 'password',
      value: passwordInputRef.current?.value || '',
    })
    setPasswordsEqual(passwordInputRef.current?.value === passwordConfirmationRef.current?.value)
  }, [handleUpdate])

  useEffect(() => {
    const isFormValid =
      passwordValidationState === PasswordValidationState.VALID && passwordsEqual && emailExists
    setFormValid(isFormValid)
  }, [passwordsEqual, emailExists, passwordValidationState])

  return (
    <MainContentContainer
      sxContainer={{
        width: '100%',
        maxWidth: ['100%', '48rem'],
        py: '4rem',
        px: ['1.6rem', '2.4rem'],
        mb: 0,
      }}
    >
      <Flex
        as="form"
        sx={{
          width: '100%',
          position: 'relative',
          height: 'auto',
        }}
        data-testid="login-form"
      >
        <Flex
          sx={{
            flexDirection: 'column',
            width: '100%',
            gap: '2.4rem',
            alignItems: 'center',
          }}
        >
          <LoginSignUpGoogleButton />
          <DividerWithText
            text="OR"
            sx={{
              gap: '1.6rem',
              span: {
                color: 'white',
              },
            }}
          />
          <Input
            isCompact
            color="aliceBlue"
            label="Your Email Address"
            id="email"
            type="email"
            name="email"
            data-testid="create-account-email"
            onChange={(e): void => handleUpdate(e.target)}
            ref={emailInputRef}
          />
          <Flex
            sx={{
              width: '100%',
              flexDirection: 'column',
              gap: '0.8rem',
            }}
          >
            <Flex
              sx={{
                flexDirection: ['column', 'row'],
                gap: '2.4rem',
                width: '100%',
              }}
            >
              <PasswordInput
                isCompact
                color="aliceBlue"
                label="Password"
                id="password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                data-testid="create-account-password"
                onChange={(e): void => {
                  setPassword(e.target.value)
                  handleUpdate(e.target)
                }}
                ref={passwordInputRef}
              />

              <PasswordInput
                isCompact
                labelColor={passwordHint === "Passwords don't match!" ? 'pomegranate' : undefined}
                color="white"
                label="Confirm Password"
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                data-testid="create-account-confirm-password"
                onChange={(e): void => {
                  setConfirmPassword(e.target.value)
                  handleUpdate(e.target)
                }}
                ref={passwordConfirmationRef}
                shouldShowShowPasswordButton
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                sxShowPasswordStyle={{
                  bottom: '0.7rem',
                  right: '1rem',
                }}
                sx={{
                  pr: '4rem',
                }}
              />
            </Flex>
            <Flex
              sx={{
                fontSize: '1.2rem',
              }}
              color={
                passwordValidationState !== PasswordValidationState.VALID ||
                passwordHint === "Passwords don't match!"
                  ? 'pomegranate'
                  : 'white50'
              }
              tabIndex={passwordHint ? 0 : -1}
            >
              {passwordHint}
            </Flex>
          </Flex>
          <Checkbox
            text={
              <>
                I read and agree to the{' '}
                <ExternalLink to="/terms" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                  Terms of Service
                </ExternalLink>
                .
              </>
            }
            isChecked={isChecked}
            labelSx={{
              textAlign: 'left',
              ml: '0.4rem',
              width: '100%',
            }}
            onChange={newValue => setIsChecked(newValue)}
          />
          {children}
          <Button
            variant="gradientButton"
            sx={{
              minWidth: '100%',
              height: ['4.8rem', '4.7rem'],
            }}
            disabled={!formValid || !isChecked}
            onClick={formValid && !isRegistrationInProgress ? onNextClick : undefined}
            data-testid="create-account-continue-button"
            ariaLabel="create account button"
          >
            {isRegistrationInProgress ? (
              <Flex sx={{ alignItems: 'center', justifyContent: 'center' }}>
                <Spinner
                  color="aliceBlue"
                  sx={{
                    height: '2.4rem',
                  }}
                />
                <Text variant="size17Weight500Line138">Creating account...</Text>
              </Flex>
            ) : (
              <>
                <Text variant="size17Weight500Line138">Create Personal Account</Text>
                <Svg
                  svg={ButtonArrow}
                  sx={{
                    ml: '0.6rem',
                  }}
                  descriptiveText="Create Personal Account button arrow"
                />
              </>
            )}
          </Button>
          <Button
            ariaLabel="back button"
            variant="simple"
            sx={{
              width: 'fit-content',
              display: 'flex',
              justifyContent: 'center',
              color: 'cyan800',
              p: 0,
            }}
            onClick={onPreviousClick}
          >
            <Text variant="size17Weight500Line138">Back</Text>
          </Button>
        </Flex>
      </Flex>
    </MainContentContainer>
  )
}
